const config = JSON.parse(document.getElementById('bbi-config')?.textContent || '{}');
const loaded = {};
const load = (name) => (loaded[name] ||= import(config.assets[name]));

/* ---------- Mobile menu (single nav tree, off-canvas below 1200px) ---------- */
const nav = document.querySelector('[data-menu]');
const menuToggle = document.querySelector('[data-menu-toggle]');
const menuOverlay = document.querySelector('[data-menu-overlay]');
const desktop = window.matchMedia('(min-width: 1200px)');

function setMenu(open) {
  if (!nav || !menuToggle) return;
  nav.classList.toggle('is-open', open);
  menuOverlay?.classList.toggle('is-visible', open);
  menuToggle.setAttribute('aria-expanded', String(open));
  menuToggle.setAttribute('aria-label', open ? config.strings.closeMenu : config.strings.openMenu);
  document.documentElement.classList.toggle('scroll-lock', open);
  if (open) {
    nav.querySelector('[data-menu-close]')?.focus();
  } else {
    menuToggle.focus();
  }
}

menuToggle?.addEventListener('click', () => setMenu(!nav.classList.contains('is-open')));
nav?.querySelector('[data-menu-close]')?.addEventListener('click', () => setMenu(false));
menuOverlay?.addEventListener('click', () => setMenu(false));

nav?.addEventListener('keydown', (event) => {
  if (!nav.classList.contains('is-open')) return;
  if (event.key === 'Escape') {
    setMenu(false);
    return;
  }
  if (event.key !== 'Tab') return;
  const focusable = [...nav.querySelectorAll('a[href], button, summary')].filter((el) => el.offsetParent !== null);
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
});

desktop.addEventListener('change', () => {
  if (desktop.matches && nav?.classList.contains('is-open')) setMenu(false);
});

/* ---------- Desktop dropdowns: one open at a time, close on outside click / Escape ---------- */
const dropdowns = [...document.querySelectorAll('[data-menu-details]')];

dropdowns.forEach((details) => {
  details.addEventListener('toggle', () => {
    if (details.open && desktop.matches) {
      dropdowns.forEach((other) => other !== details && (other.open = false));
    }
  });
});

document.addEventListener('click', (event) => {
  if (!desktop.matches) return;
  dropdowns.forEach((details) => {
    if (details.open && !details.contains(event.target)) details.open = false;
  });
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || !desktop.matches) return;
  const open = dropdowns.find((details) => details.open);
  if (open) {
    open.open = false;
    open.querySelector('summary')?.focus();
  }
});

/* ---------- Header search: inline field with a live results panel; logic loads on first use ---------- */
document.querySelectorAll('[data-search]').forEach((root) => {
  const input = root.querySelector('[data-search-input]');
  const panel = root.querySelector('[data-search-panel]');
  if (!input || !panel) return;

  const open = () => {
    if (!panel.hidden) return;
    panel.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    load('search').then((module) => module.init(root, config));
  };
  const close = () => {
    if (panel.hidden) return;
    panel.hidden = true;
    input.setAttribute('aria-expanded', 'false');
  };

  input.addEventListener('focus', open);
  input.addEventListener('input', open);
  root.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !panel.hidden) {
      close();
      input.focus();
    }
  });
  root.addEventListener('focusout', (event) => {
    if (!root.contains(event.relatedTarget)) close();
  });
  document.addEventListener('click', (event) => {
    if (!root.contains(event.target)) close();
  });
});

function focusSearch(event) {
  const input = document.querySelector('[data-search-input]');
  if (!input) return;
  event.preventDefault();
  if (nav?.classList.contains('is-open')) setMenu(false);
  input.scrollIntoView({ block: 'center' });
  input.focus();
}

/* ---------- Cart: drawer logic loads on first interaction ---------- */
const drawerEnabled = () => config.cartType === 'drawer' && document.querySelector('[data-cart-drawer]');

document.addEventListener('click', (event) => {
  const searchTrigger = event.target.closest('[data-search-open]');
  if (searchTrigger) {
    focusSearch(event);
    return;
  }

  const cartTrigger = event.target.closest('[data-cart-open]');
  if (cartTrigger && drawerEnabled()) {
    event.preventDefault();
    load('cart').then((module) => module.open());
    return;
  }

  if (event.target.closest('#CartRoot [data-qty-change]')) {
    event.preventDefault();
    load('cart').then((module) => module.handleClick(event));
  }
});

document.addEventListener('change', (event) => {
  if (event.target.matches('#CartRoot [data-qty-input]')) {
    load('cart').then((module) => module.handleInput(event.target));
  }
});

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  const action = form.getAttribute('action') || '';
  if (!action.endsWith(config.routes.cartAdd) || !drawerEnabled()) return;
  event.preventDefault();
  load('cart').then((module) => module.add(form, event.submitter));
});

/* ---------- Announcement bar ---------- */
document.querySelectorAll('[data-announcement]').forEach((bar) => {
  const messages = [...bar.querySelectorAll('.announcement__message')];
  const toggle = bar.querySelector('[data-announcement-toggle]');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let index = 0;
  let timer = null;
  let paused = bar.dataset.autoplay !== 'true' || reduceMotion;

  const show = (next) => {
    messages[index].hidden = true;
    index = (next + messages.length) % messages.length;
    messages[index].hidden = false;
  };

  const stop = () => {
    clearInterval(timer);
    timer = null;
  };

  const start = () => {
    stop();
    if (!paused) timer = setInterval(() => show(index + 1), Number(bar.dataset.speed || 6) * 1000);
  };

  const setPaused = (value) => {
    paused = value;
    if (toggle) {
      toggle.setAttribute('aria-label', paused ? config.strings.play : config.strings.pause);
      toggle.setAttribute('aria-pressed', String(paused));
    }
    start();
  };

  bar.querySelector('[data-announcement-prev]')?.addEventListener('click', () => show(index - 1));
  bar.querySelector('[data-announcement-next]')?.addEventListener('click', () => show(index + 1));
  toggle?.addEventListener('click', () => setPaused(!paused));
  bar.addEventListener('mouseenter', stop);
  bar.addEventListener('mouseleave', start);
  bar.addEventListener('focusin', stop);
  bar.addEventListener('focusout', start);

  if (toggle && reduceMotion) setPaused(true);
  start();
});

/* ---------- Slideshow: native scroll-snap, JS only adds dots, arrows and autoplay ---------- */
document.querySelectorAll('[data-slideshow]').forEach((show) => {
  const track = show.querySelector('[data-slideshow-track]');
  const slides = [...track.children];
  const dots = [...show.querySelectorAll('[data-slideshow-dot]')];
  const toggle = show.querySelector('[data-slideshow-toggle]');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let index = 0;
  let timer = null;
  let paused = show.dataset.autoplay !== 'true' || reduceMotion;

  const goTo = (next, smooth = true) => {
    index = (next + slides.length) % slides.length;
    track.scrollTo({ left: slides[index].offsetLeft - track.offsetLeft, behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
  };

  const sync = () => {
    const current = Math.round(track.scrollLeft / track.clientWidth);
    if (current === index && dots[current]?.getAttribute('aria-current') === 'true') return;
    index = Math.min(slides.length - 1, Math.max(0, current));
    dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
  };

  let scrollTick = false;
  track.addEventListener('scroll', () => {
    if (scrollTick) return;
    scrollTick = true;
    requestAnimationFrame(() => {
      scrollTick = false;
      sync();
    });
  }, { passive: true });

  const stop = () => {
    clearInterval(timer);
    timer = null;
  };
  const start = () => {
    stop();
    if (!paused && !document.hidden) timer = setInterval(() => goTo(index + 1), Number(show.dataset.speed || 5) * 1000);
  };

  show.querySelector('[data-slideshow-prev]')?.addEventListener('click', () => goTo(index - 1));
  show.querySelector('[data-slideshow-next]')?.addEventListener('click', () => goTo(index + 1));
  dots.forEach((dot) => dot.addEventListener('click', () => goTo(Number(dot.dataset.slideshowDot))));
  toggle?.addEventListener('click', () => {
    paused = !paused;
    toggle.setAttribute('aria-pressed', String(paused));
    toggle.setAttribute('aria-label', paused ? config.strings.playSlideshow : config.strings.pauseSlideshow);
    start();
  });
  show.addEventListener('mouseenter', stop);
  show.addEventListener('mouseleave', start);
  show.addEventListener('focusin', stop);
  show.addEventListener('focusout', start);
  show.addEventListener('touchstart', stop, { passive: true });
  document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
  if (toggle && paused) toggle.setAttribute('aria-pressed', 'true');
  start();
});

/* ---------- Store locator city filter ---------- */
document.querySelectorAll('[data-stores]').forEach((root) => {
  const buttons = [...root.querySelectorAll('[data-store-city]')];
  const stores = [...root.querySelectorAll('[data-city]')];
  buttons.forEach((button) => {
    button.addEventListener('click', () => {
      const city = button.dataset.storeCity;
      buttons.forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
      stores.forEach((store) => {
        store.hidden = city !== '' && store.dataset.city !== city;
      });
    });
  });
});

/* ---------- Back to top ---------- */
const backToTop = document.querySelector('[data-back-to-top]');
if (backToTop) {
  let ticking = false;
  const update = () => {
    ticking = false;
    backToTop.hidden = window.scrollY < window.innerHeight * 1.5;
  };
  window.addEventListener('scroll', () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(update);
    }
  }, { passive: true });
  update();
}

/* ---------- Wishlist: saved in this browser (localStorage), shared by every heart on the page ---------- */
const WISHLIST_KEY = 'bbi:wishlist';
const readWishlist = () => {
  try {
    const list = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    return Array.isArray(list) ? list.filter((h) => typeof h === 'string') : [];
  } catch {
    return [];
  }
};
const writeWishlist = (list) => {
  try {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(list.slice(0, 100)));
  } catch {
    /* storage unavailable: hearts still toggle for this visit */
  }
  document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { list } }));
};
let wishlistMemory = readWishlist();

function syncWishlist() {
  const list = wishlistMemory;
  document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
    const on = list.includes(button.dataset.handle);
    button.setAttribute('aria-pressed', String(on));
    const label = on ? button.dataset.labelRemove : button.dataset.labelAdd;
    if (label) button.setAttribute('aria-label', label);
  });
  document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
    el.textContent = String(list.length);
    el.hidden = list.length === 0;
  });
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-wishlist-toggle]');
  if (!button) return;
  event.preventDefault();
  const handle = button.dataset.handle;
  if (!handle) return;
  wishlistMemory = wishlistMemory.includes(handle) ? wishlistMemory.filter((h) => h !== handle) : [handle, ...wishlistMemory];
  writeWishlist(wishlistMemory);
  syncWishlist();
});

window.addEventListener('storage', (event) => {
  if (event.key !== WISHLIST_KEY) return;
  wishlistMemory = readWishlist();
  syncWishlist();
});
document.addEventListener('wishlist:sync', syncWishlist);
syncWishlist();

window.BBI = Object.assign(window.BBI || {}, {
  wishlist: { get: () => [...wishlistMemory], set: (list) => { wishlistMemory = list; writeWishlist(list); syncWishlist(); } },
  loadCart: () => load('cart'),
  config,
});

/* ---------- Tabs (product tabs, goal finder steps): roving focus, arrow keys ---------- */
document.querySelectorAll('[data-tabs]').forEach((root) => {
  const tabs = [...root.querySelectorAll('[role="tab"]')];
  const select = (tab, focus = false) => {
    tabs.forEach((t) => {
      const on = t === tab;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
      const panel = document.getElementById(t.getAttribute('aria-controls'));
      if (panel) panel.hidden = !on;
    });
    if (focus) tab.focus();
  };
  tabs.forEach((tab, i) => {
    tab.addEventListener('click', () => select(tab));
    tab.addEventListener('keydown', (event) => {
      const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
      if (event.key in keys) {
        event.preventDefault();
        select(tabs[(i + keys[event.key] + tabs.length) % tabs.length], true);
      } else if (event.key === 'Home') {
        event.preventDefault();
        select(tabs[0], true);
      } else if (event.key === 'End') {
        event.preventDefault();
        select(tabs[tabs.length - 1], true);
      }
    });
  });
});

/* ---------- Stacks page: filter ready-made stacks by goal ---------- */
document.querySelectorAll('[data-stacks]').forEach((root) => {
  const buttons = [...root.querySelectorAll('[data-stack-filter]')];
  if (!buttons.length) return;
  const items = [...root.querySelectorAll('.stacks__item')];
  const status = root.querySelector('[data-stack-status]');
  buttons.forEach((button) => {
    button.addEventListener('click', () => {
      const goal = button.dataset.stackFilter;
      buttons.forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
      let shown = 0;
      items.forEach((item) => {
        const match = goal === '' || item.querySelector('[data-stack-card]')?.dataset.goal === goal;
        item.hidden = !match;
        if (match) shown += 1;
      });
      if (status) status.textContent = `${shown} · ${button.textContent.trim()}`;
    });
  });
});

/* ---------- Page modules: load only on pages that use them ---------- */
if (document.querySelector('[data-goal-finder]')) load('goalFinder').then((m) => m.init());
if (document.querySelector('[data-stack-builder]')) load('stackBuilder').then((m) => m.init());
if (document.querySelector('[data-wishlist-page]')) load('wishlistPage').then((m) => m.init());
